import { useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { useLocation } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import { useAuth } from '@/providers/auth'
import { companyConfigQueryKey, updateCompanyConfig } from '@/lib/companies'
import { CONFETTI_COLORS, burstConfetti } from '@/lib/confetti'
import { errorMessage } from '@/lib/errors'
import { plural } from '@/lib/format'
import {
  MISSION_COPY,
  claimMission,
  missionsQueryKey,
  syncMissions,
  unseenCelebration,
  type MissionBoard,
  type MissionId,
  type MissionLevelUp,
  type MissionSyncResult
} from '@/lib/missions'
import type { CompanyConfig } from '@/pages/Companies/GetCompany/types/config'

const SYNC_STALE_MS = 30_000

export interface LevelUpCelebration {
  levelUps: MissionLevelUp[]
  board: MissionBoard
}

const seen = new Set<string>()
const listeners = new Set<() => void>()
let celebration: LevelUpCelebration | null = null

const emit = () => listeners.forEach(listener => listener())

function celebrate(companyId: string, result: MissionSyncResult) {
  const { completed, levelUps, keys } = unseenCelebration(companyId, result, seen)
  keys.forEach(key => seen.add(key))
  if (completed.length === 0 && levelUps.length === 0) return

  if (levelUps.length === 0) burstConfetti(CONFETTI_COLORS, 90)
  const points = completed.reduce((sum, id) => sum + (result.board.missions.find(mission => mission.id === id)?.points ?? 0), 0)
  if (completed.length > 2) toast.success(plural(completed.length, 'missão concluída', 'missões concluídas'), { description: `+${points} pontos` })
  else
    for (const id of completed)
      toast.success(`Missão concluída: ${MISSION_COPY[id].title.toLowerCase()}`, {
        description: `+${result.board.missions.find(mission => mission.id === id)?.points ?? 0} pontos`
      })

  if (levelUps.length > 0) {
    celebration = { levelUps, board: result.board }
    emit()
  }
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

const currentCelebration = () => celebration

export function useLevelUpCelebration() {
  const value = useSyncExternalStore(subscribe, currentCelebration, currentCelebration)
  const dismiss = () => {
    celebration = null
    emit()
  }
  return { celebration: value, dismiss }
}

export function useMissions(enabled = true) {
  const { company } = useAuth()
  const { pathname } = useLocation()
  const queryClient = useQueryClient()
  const queryKey = missionsQueryKey(company.id)
  const sync = useQuery(queryKey, () => syncMissions(company.id), { enabled: enabled && !!company.id, staleTime: SYNC_STALE_MS })
  const [enablingChat, setEnablingChat] = useState(false)

  useEffect(() => {
    if (enabled && sync.dataUpdatedAt && Date.now() - sync.dataUpdatedAt > SYNC_STALE_MS) void sync.refetch()
  }, [pathname])

  useEffect(() => {
    if (sync.data) celebrate(company.id, sync.data)
  }, [sync.data, company.id])

  const claim = useMutation<MissionSyncResult, unknown, MissionId>(id => claimMission(id, company.id), {
    onSuccess: result => {
      queryClient.setQueryData(queryKey, result)
    },
    onError: error => {
      toast.error(errorMessage(error, 'Não foi possível registrar a missão agora.'))
    }
  })

  const enableChat = async () => {
    const configKey = companyConfigQueryKey(company.id)
    setEnablingChat(true)
    try {
      await updateCompanyConfig({ chat: { enabled: true } }, company.id)
      queryClient.setQueryData<CompanyConfig | undefined>(configKey, current =>
        current ? { ...current, chat: { ...current.chat, enabled: true } } : current
      )
      void queryClient.invalidateQueries(configKey)
      await sync.refetch()
    } catch (error) {
      toast.error(errorMessage(error, 'Não foi possível ativar o chat agora.'))
    } finally {
      setEnablingChat(false)
    }
  }

  return {
    board: sync.data?.board ?? null,
    ready: sync.isSuccess,
    failed: sync.isError && !sync.data,
    retry: () => void sync.refetch(),
    claim: (id: MissionId) => claim.mutate(id),
    claiming: claim.isLoading ? (claim.variables ?? null) : null,
    enableChat,
    enablingChat
  }
}

const hiddenKey = (companyId: string) => `@Habitar:next-steps-hidden:${companyId}`

export function useMissionsHidden() {
  const { company } = useAuth()
  const [hiddenFor, setHiddenFor] = useState<string[]>([])
  const storedHidden = useMemo(() => {
    try {
      return localStorage.getItem(hiddenKey(company.id)) === 'true'
    } catch {
      return false
    }
  }, [company.id])

  const hide = () => {
    setHiddenFor(current => [...current, company.id])
    try {
      localStorage.setItem(hiddenKey(company.id), 'true')
    } catch {
      return
    }
  }

  return { hidden: storedHidden || hiddenFor.includes(company.id), hide }
}
